import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUserPackage } from '../../actions';
import { api } from '../../services/apiService';
import { isUpdateApiFailure } from '../../sagas/apiFailure';
import { COUNT_QUESTION_OPTIONS, resolveDailyLimit } from './daily-limit';
import { resolveTrialOffer } from './trial-offer';

// How long the card waits for the plan read before it goes ahead with plan-neutral words.
export const PLAN_WAIT_MS = 4000;

// localStorage can be missing or throw (blocked site data, private modes).
export const readStorage = (key) => {
  try {
    const value = window.localStorage.getItem(key);
    return value && value !== 'undefined' ? value : null;
  } catch {
    return null;
  }
};

/** True once `active` has held for `ms`. Resets when `active` drops. */
const useElapsed = (active, ms) => {
  const [elapsed, setElapsed] = useState(false);
  useEffect(() => {
    setElapsed(false);
    if (!active) return undefined;
    const timer = setTimeout(() => setElapsed(true), ms);
    return () => clearTimeout(timer);
  }, [active, ms]);
  return elapsed;
};

/**
 * The student's selected plans (GET /user/{id}/plan/true, state.userPackage):
 * the set the server takes the daily allowance from. With `fetchIfMissing`, asks
 * once when nothing is in the store; the results page already asks on mount.
 * plans is null until a list arrives (or when the read failed).
 */
export const useSelectedPlans = ({ enabled, userId, fetchIfMissing = false }) => {
  const dispatch = useDispatch();
  const userPackage = useSelector((state) => state.userPackage);
  const requested = useRef(false);
  const plans = Array.isArray(userPackage?.userPackage) ? userPackage.userPackage : null;
  const settled = userPackage?.loading === false;

  useEffect(() => {
    if (enabled && fetchIfMissing && userId && !requested.current
        && userPackage?.loading === undefined && !plans) {
      requested.current = true;
      dispatch(getUserPackage(userId));
    }
  }, [enabled, fetchIfMissing, userId, userPackage?.loading, plans, dispatch]);

  return { plans, settled };
};

const OFFER_PENDING = Object.freeze({ settled: false, days: null, native: false, planId: null });
const NO_OFFER = Object.freeze({ settled: true, days: null, native: false, planId: null });

// One trial check per student and exam for a while. Create Quiz re-opens its
// modal on every count response (each filter change), and the answer does not
// change within minutes, so /trial-eligible and /plan are not asked again.
export const TRIAL_OFFER_CACHE_MS = 10 * 60 * 1000;
const trialOfferCache = new Map();
export const clearTrialOfferCache = () => trialOfferCache.clear();

const trialOfferEntry = (userId, exam) => {
  const key = `${userId}|${exam || ''}`;
  const cached = trialOfferCache.get(key);
  if (cached && Date.now() - cached.at < TRIAL_OFFER_CACHE_MS) {
    return cached;
  }
  const entry = { at: Date.now(), result: null, promise: null };
  entry.promise = resolveTrialOffer({ userId, exam }).then((result) => {
    entry.result = result;
    return result;
  }, (error) => {
    // resolveTrialOffer never rejects; if it ever did, don't keep the failure.
    trialOfferCache.delete(key);
    throw error;
  });
  trialOfferCache.set(key, entry);
  return entry;
};

const freshCachedOffer = (userId, exam) => {
  const cached = trialOfferCache.get(`${userId}|${exam || ''}`);
  return cached?.result && Date.now() - cached.at < TRIAL_OFFER_CACHE_MS ? cached.result : null;
};

/**
 * The trial offer for a student (see trial-offer.js). settled is false while it
 * is checked; it always settles (a failed check settles as "no trial").
 */
export const useTrialOffer = ({ enabled, userId, exam = null }) => {
  // Settled from the first render when there is nothing to check (paid plans)
  // or the answer is cached.
  const [offer, setOffer] = useState(() => {
    if (!enabled) return NO_OFFER;
    const cached = freshCachedOffer(userId, exam);
    return cached ? { settled: true, ...cached } : OFFER_PENDING;
  });
  useEffect(() => {
    if (!enabled) {
      setOffer(NO_OFFER);
      return undefined;
    }
    let cancelled = false;
    const entry = trialOfferEntry(userId, exam);
    if (entry.result) {
      setOffer({ settled: true, ...entry.result });
      return undefined;
    }
    setOffer(OFFER_PENDING);
    entry.promise
      .then((result) => {
        if (!cancelled) setOffer({ settled: true, ...result });
      })
      .catch(() => {
        if (!cancelled) setOffer(NO_OFFER);
      });
    return () => { cancelled = true; };
  }, [enabled, userId, exam]);
  return offer;
};

/**
 * The results-page read of today's allowance. One count request, made directly
 * (not through the quesCount store slice, which Create Quiz owns). The count
 * itself is ignored: only questionLeftInDay and questionInDayLimit are read.
 *
 * @returns {{ status: 'off'|'loading'|'failed'|'ready', info: object|null }}
 *   info is resolveDailyLimit's result once ready.
 */
export const useResultDailyLimit = ({ enabled, userId, examTypes }) => {
  const examList = Array.isArray(examTypes) ? examTypes.filter((exam) => typeof exam === 'string' && exam) : [];
  const examKey = examList.join('|');
  const singleExam = examList.length === 1 ? examList[0] : null;
  // The count endpoint needs a non-empty category map; without the quiz's exam
  // types there is nothing sensible to send, so the page keeps its plain button.
  const active = Boolean(enabled && userId && examList.length);

  const [count, setCount] = useState({ status: 'idle', value: null });
  useEffect(() => {
    if (!active) {
      setCount({ status: 'idle', value: null });
      return undefined;
    }
    let cancelled = false;
    setCount({ status: 'loading', value: null });
    void (async () => {
      let value = null;
      try {
        const res = await api.getQuestionCount({
          userId,
          questionOptions: { ...COUNT_QUESTION_OPTIONS },
          categoryMap: { examType: examKey.split('|') },
          quizOptions: { thresholdEnabled: false },
        });
        value = res && typeof res === 'object' && !isUpdateApiFailure(res) ? res : null;
      } catch {
        value = null;
      }
      if (!cancelled) setCount({ status: value ? 'ready' : 'failed', value });
    })();
    return () => { cancelled = true; };
  }, [active, userId, examKey]);

  const { plans, settled } = useSelectedPlans({ enabled: active, userId });
  const planWaitOver = useElapsed(active && count.status === 'ready' && !settled, PLAN_WAIT_MS);

  if (!active) return { status: 'off', info: null };
  if (count.status === 'failed') return { status: 'failed', info: null };
  if (count.status !== 'ready' || (!settled && !planWaitOver)) return { status: 'loading', info: null };
  const info = resolveDailyLimit({ count: count.value, plans, exam: singleExam });
  return info ? { status: 'ready', info } : { status: 'failed', info: null };
};
